import type { JewelsResponse, OwnedJewel } from '@kenocyber/shared';
import { JewelCard } from './JewelCard';
import { JewelElectricFilters } from './JewelElectricFilters';

/**
 * A secção "Joias" do inventário (gate V28C).
 *
 * ## Uma carta por INSTÂNCIA, nunca por definição
 *
 * Duas cópias da mesma joia são duas coisas que o jogador tem, e podem estar em
 * sítios diferentes. Agrupar por definição esconderia exatamente isso - e é o
 * mesmo erro que a lista de skins já não comete.
 *
 * ## A soberana tem secção própria, porque é outra coisa
 *
 * Não ocupa socket e não tem botão de encaixar. Pô-la na mesma grelha com um
 * botão desativado seria oferecer um gesto que não existe; separá-la e dizer o
 * que ela faz é a resposta honesta.
 *
 * ## O que esta secção NÃO mostra
 *
 * Chance, preço, upgrade ou promessa de futuro. As catorze não concedem nada
 * nesta fase, e a carta não insinua que concederão: mostra o nome, o nível e o
 * estado, que é tudo o que é verdade hoje.
 */

/**
 * De onde a arte das joias é servida.
 *
 * A mesma raiz que o resto do jogo já usa. O caminho de cada peça vem do
 * MANIFESTO, no campo `assetFile`, e nunca é construído a partir de um id -
 * um caminho montado à mão é um caminho que sobrevive a um ficheiro que mudou
 * de nome.
 */
export const JEWEL_ASSET_BASE_URL = '/game-assets/';

export interface JewelPanelProps {
  readonly view: JewelsResponse | null;
  readonly assetBaseUrl: string;
  /** O nome legível de cada cópia de skin, para dizer ONDE uma joia está. */
  readonly skinLabels: ReadonlyMap<string, string>;
  readonly loading?: boolean;
  readonly error?: string | null;
  /** A peça escolhida para o próximo encaixe, se houver uma. */
  readonly selectedInstanceId?: string | null;
  readonly onSelect?: (instanceId: string) => void;
  /**
   * Abre a incorporação para ESTA cópia de Coração (gate V28F.C).
   *
   * Por instância, nunca por definição: duas cópias são duas coisas que o
   * jogador tem, e a que ele vai gastar é a que ele carregou.
   */
  readonly onIncorporate?: (jewelInstanceId: string) => void;
}

/**
 * Existe algum assento livre, em alguma cópia de skin?
 *
 * Uma capacidade NÃO REGISTADA conta como "pode haver": afirmar que uma peça
 * está indisponível por causa de uma cópia cuja capacidade ninguém sabe seria
 * inventar a resposta. A dúvida deixa a carta disponível e o servidor decide.
 */
function hasFreeSeat(view: JewelsResponse): boolean {
  return view.skins.some(
    (skin) => skin.sockets === null || skin.sockets.some((seat) => seat.jewelInstanceId === null),
  );
}

/** "Encaixada em Silver Hunter, espaço 2" - identidade, e nunca um uuid. */
function seatLabelFor(jewel: OwnedJewel, skinLabels: ReadonlyMap<string, string>): string | null {
  if (!jewel.seat) {
    return null;
  }
  const skin = skinLabels.get(jewel.seat.skinInstanceId) ?? 'uma skin';
  /* O índice é mostrado a começar em 1: o assento 0 existe no contrato, e um
     jogador não conta a partir de zero. A identidade enviada continua a ser o
     índice real - ver `JewelSocketRow`. */
  return `Encaixada em ${skin}, espaço ${jewel.seat.socketIndex + 1}`;
}

export function JewelPanel({
  view,
  assetBaseUrl,
  skinLabels,
  loading = false,
  error = null,
  selectedInstanceId = null,
  onSelect,
  onIncorporate,
}: JewelPanelProps) {
  if (error) {
    return (
      <section className="jewel-section" data-testid="jewel-panel">
        <p className="jewel-empty" role="alert">
          {error}
        </p>
      </section>
    );
  }
  if (loading || !view) {
    return (
      <section className="jewel-section" data-testid="jewel-panel">
        <p className="jewel-empty">A carregar as joias…</p>
      </section>
    );
  }

  const sovereign = view.owned.filter((jewel) => jewel.socketMode === 'sovereign');
  const socketed = view.owned.filter((jewel) => jewel.socketMode === 'socketed');
  /*
   * Sem assento livre em lado nenhum, uma peça livre não tem para onde ir.
   *
   * A carta diz-lhe isso por palavras em vez de oferecer uma escolha que
   * acabaria numa recusa do servidor.
   */
  const noSeat = hasFreeSeat(view) ? null : 'sem espaço livre em nenhuma skin';

  return (
    <section className="jewel-section" data-testid="jewel-panel">
      {/*
       * Os filtros elétricos, montados UMA vez para as quinze cartas.
       *
       * Aqui e não dentro da carta: quinze cartas dariam quinze SVG com quinze
       * conjuntos de ids, que é exatamente o que a implementação original faz e
       * a razão de ela não poder ser usada como está.
       */}
      <JewelElectricFilters />
      {sovereign.length > 0 ? (
        <div className="jewel-sovereign-section" data-testid="jewel-sovereign-section">
          {/*
           * A carta primeiro, o texto ao lado.
           *
           * O destaque é HORIZONTAL e baixo: era uma caixa alta com uma peça
           * sozinha, e a 1280x720 empurrava a prateleira inteira para fora do
           * ecrã. A peça continua excecional - o que ela não precisa de ser é
           * grande.
           */}
          {/*
           * As cópias vivem numa prateleira PRÓPRIA, e não soltas na secção.
           *
           * A secção é uma grelha de duas colunas - `auto minmax(0, 1fr)` -
           * desenhada para UMA carta soberana ao lado do seu texto. Enquanto
           * cada cópia era filha directa, elas caíam alternadamente na coluna
           * que encolhe e na que estica: uma célula com 191px ao lado de outra
           * com 749px, e gastar um Coração trocava a paridade e rearranjava a
           * prateleira inteira.
           *
           * Com este contentor a secção volta a ter dois filhos, que é o que
           * ela sempre soube arrumar, e a igualdade entre células passa a ser
           * um problema da prateleira - onde tem solução.
           */}
          <div className="jewel-sovereign-shelf" data-testid="jewel-sovereign-shelf">
            {sovereign.map((jewel) => (
              <div className="jewel-sovereign-copy-item" key={jewel.instanceId}>
                <JewelCard jewel={jewel} assetBaseUrl={assetBaseUrl} seatLabel="No inventário" />
                {/*
                 * Um botão POR CÓPIA, e é a razão de ele viver aqui.
                 *
                 * A operação gasta uma instância nomeada; um único botão ao lado
                 * do texto teria de adivinhar qual das cópias é, e adivinhar qual
                 * se gasta numa operação irreversível é o erro que não se desfaz.
                 */}
                {onIncorporate ? (
                  <button
                    type="button"
                    className="jewel-incorporate-button"
                    data-testid={`jewel-incorporate-${jewel.instanceId}`}
                    onClick={() => onIncorporate(jewel.instanceId)}
                  >
                    INCORPORAR EM UMA NAVE
                  </button>
                ) : null}
              </div>
            ))}
          </div>
          <div className="jewel-sovereign-copy">
            <h3 className="jewel-sovereign-title">Soberana</h3>
            {/*
             * O texto é o contrato com o jogador, e é literal de propósito: diz
             * o que a peça faz, o que ela NÃO faz, e o que ainda não existe.
             * Cada uma dessas quatro frases é verdade hoje e verificável no
             * código.
             *
             * ## Porque é que ele mudou no gate V28F.A
             *
             * Dizia "Ativa automaticamente. (...) +1% de dano na caça." Era
             * verdade quando o efeito vinha da POSSE, e deixou de ser: um
             * Coração Primordial é INCORPORADO numa nave e consumido ao fazê-lo,
             * e guardado no inventário não concede nada. Deixar a frase antiga
             * seria mostrar ao jogador um bónus que ele não tem.
             *
             * ## E porque é que ela mudou outra vez no gate V28F.C
             *
             * A última frase dizia "A incorporação ainda não está disponível."
             * Era verdade enquanto a operação existia só no servidor. Agora tem
             * botão, e a frase que a substitui é a que o jogador precisa ANTES
             * de carregar nele: que o gesto não se desfaz.
             */}
            <p className="jewel-sovereign-note" data-testid="jewel-sovereign-note">
              Não ocupa socket. Incorpora-se numa nave e é consumida ao fazê-lo. Guardada no
              inventário não concede nada. A incorporação é permanente.
            </p>
          </div>
        </div>
      ) : null}

      <div className="jewel-grid" data-testid="jewel-grid">
        {socketed.map((jewel) => (
          <JewelCard
            key={jewel.instanceId}
            jewel={jewel}
            assetBaseUrl={assetBaseUrl}
            seatLabel={seatLabelFor(jewel, skinLabels)}
            selected={jewel.instanceId === selectedInstanceId}
            unavailable={jewel.seat === null ? noSeat : null}
            onSelect={onSelect ? () => onSelect(jewel.instanceId) : undefined}
          />
        ))}
      </div>
      {socketed.length === 0 && sovereign.length === 0 ? (
        <p className="jewel-empty">Você ainda não tem joias.</p>
      ) : null}
    </section>
  );
}
